import { useEffect, useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useToasts } from '@/hooks/useToasts'

import {
  useCheckpointTasksQuery,
  useCreateCheckpointTaskMutation,
  useDeleteCheckpointTaskMutation,
  useUpdateCheckpointMutation,
  useUpdateCheckpointTaskMutation,
  useUpdateDeviceMutation,
} from '@/graphql/generated/hooks'
import {
  CreateCheckpointTaskInput,
  UpdateCheckpointTaskInput,
} from '@/graphql/generated/schemas'
import { getErrorMessage } from '@/utils/errorMessage'

import { CheckpointTaskInputI } from '../types/types'
import { getChecklistItems, hasDescriptionUpdated } from '../utils/utils'

const REFETCH_QUERIES = [
  'FloorDevicesCount',
  'FacilityDevicesByFloors',
  'FloorsFacilityCheckpoints',
]

interface UseUpdateCheckpointIProps {
  checkpointId: string
  isPhotoRequiredDefault: boolean
  onDone: () => void
}

export const useUpdateCheckpoint = ({
  checkpointId,
  isPhotoRequiredDefault,
  onDone,
}: UseUpdateCheckpointIProps) => {
  const { showSuccess, showError } = useToasts()
  const [isLoading, setIsLoading] = useState(false)
  const [areTasksRequired, setAreTasksRequired] = useState(false)
  const [isPhotoRequired, setIsPhotoRequired] = useState(isPhotoRequiredDefault)
  const [hasPhotoRequiredChanged, setHasPhotoRequiredChanged] = useState(false)
  const isGuardToursV2 = useFeatureFlag(FeatureFlag.guardToursV2)

  const { data, loading: areCheckpointTasksLoading } = useCheckpointTasksQuery({
    variables: { checkpointId },
    fetchPolicy: 'network-only',
    skip: !isGuardToursV2,
  })
  const [updateDevice] = useUpdateDeviceMutation()
  const [updateCheckpointPhotoRequired] = useUpdateCheckpointMutation()
  const [createCheckpointTask] = useCreateCheckpointTaskMutation()
  const [deleteCheckpointTask] = useDeleteCheckpointTaskMutation()
  const [updateCheckpointTask] = useUpdateCheckpointTaskMutation()

  const checkpointTasks = useMemo(
    () => data?.checkpointTasks?.edges?.map((edge) => edge.node) || [],
    [data]
  )
  const existingChecklistValues = useMemo(
    () => getChecklistItems(checkpointTasks),
    [checkpointTasks]
  )
  const areOrginalTasksRequired = useMemo(
    () => checkpointTasks.some((task) => task.isRequired) || false,
    [checkpointTasks]
  )

  // Check if Required toggle value has updated (Used for enabling/disabling save btn)
  const hasRequiredChanged = areTasksRequired !== areOrginalTasksRequired

  const updateCheckpoint = async (
    id: string,
    name: string,
    areTasksRequired: boolean,
    tasks: CheckpointTaskInputI[]
  ) => {
    try {
      setIsLoading(true)
      await updateDevice({
        variables: { input: { id, name } },
        refetchQueries: REFETCH_QUERIES,
      })

      if (isGuardToursV2) {
        await handleCreateUpdateCheckpointTasks(id, areTasksRequired, tasks)
        await handleDeleteCheckpointTask(tasks)
        // Only update if isPhotoRequired was changed
        if (hasPhotoRequiredChanged) {
          await updateCheckpointPhotoRequired({
            variables: { input: { id, isPhotoRequired } },
          })
        }
      }

      showSuccess('Checkpoint updated.')
      setIsLoading(false)

      onDone()
    } catch (e) {
      setIsLoading(false)
      showError(getErrorMessage(e))
    }
  }

  const handleCreateUpdateCheckpointTasks = async (
    checkpointId: string,
    isRequired: boolean,
    tasks: CheckpointTaskInputI[]
  ) => {
    const promises = tasks.map(async (task) => {
      const input = {
        // If the task has an id it is already created so use its id (Used for UPDATE)
        // Otherwise use checkpoint id (Used for CREATE)
        ...(task.id ? { id: task.id } : { checkpointId }),
        description: task.value,
        isRequired,
      }

      if (task.id) {
        // Only update checkpoint tasks which have been modified
        if (
          hasRequiredChanged ||
          hasDescriptionUpdated(task, checkpointTasks)
        ) {
          // Update CheckpointTask
          return updateCheckpointTask({
            variables: { input: input as UpdateCheckpointTaskInput },
          })
        }
      } else {
        // Create CheckpointTask
        return createCheckpointTask({
          variables: { input: input as CreateCheckpointTaskInput },
        })
      }
    })
    return Promise.all(promises)
  }

  const handleDeleteCheckpointTask = async (tasks: CheckpointTaskInputI[]) => {
    // Find which tasks have been removed
    const deletedTasks =
      checkpointTasks?.filter((savedTask) =>
        tasks?.every((task) => task.id !== savedTask.id)
      ) || []
    const promises = deletedTasks.map(async (task) => {
      // Delete CheckpointTask
      return deleteCheckpointTask({
        variables: { input: { id: task.id } },
      })
    })
    return Promise.all(promises)
  }

  const handleIsPhotoRequired = (isPhotoRequired: boolean) => {
    const hasChanged = isPhotoRequiredDefault !== isPhotoRequired
    setHasPhotoRequiredChanged(hasChanged)
    setIsPhotoRequired(isPhotoRequired)
  }

  useEffect(() => {
    if (checkpointTasks?.length > 0) {
      const hasRequiredTasks = checkpointTasks.some((task) => task.isRequired)
      setAreTasksRequired(hasRequiredTasks)
    }
  }, [checkpointTasks])

  return {
    areTasksRequired,
    areCheckpointTasksLoading,
    checkpointTasks,
    existingChecklistValues,
    hasRequiredChanged,
    hasPhotoRequiredChanged,
    isLoading,
    isPhotoRequired,
    handleIsPhotoRequired,
    setAreTasksRequired,
    updateCheckpoint,
    isGuardToursV2,
  }
}
